/**
 * Confirmation Modal Component
 * 
 * A reusable confirmation dialog for delete and other destructive actions.
 * Replaces insecure browser prompts with a styled frontend modal.
 * 
 * @module ConfirmationModal
 */

import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, X } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

/**
 * Modal Overlay
 */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

/**
 * Modal Container
 */
const ModalContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.xl};
  max-width: 450px;
  width: 100%;
  padding: ${Spacing.xl};
  position: relative;
`;

/**
 * Modal Header
 */
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const ModalTitle = styled.h3`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.text.secondary};
  transition: color 0.2s ease;
  border-radius: ${BorderRadius.sm};

  &:hover {
    color: ${ThemeColors.text.primary};
    background: ${ThemeColors.gray[100]};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * Modal Body
 */
const ModalBody = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const ModalMessage = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

/**
 * Modal Actions
 */
const ModalActions = styled.div`
  display: flex;
  gap: ${Spacing.md};
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  padding: ${Spacing.md} ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${ThemeColors.error.main};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.error.dark};
            transform: translateY(-1px);
            box-shadow: ${Shadows.md};
          }
        `;
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.primary.dark};
            transform: translateY(-1px);
            box-shadow: ${Shadows.md};
          }
        `;
      default:
        return `
          background: ${ThemeColors.gray[200]};
          color: ${ThemeColors.text.primary};
          
          &:hover:not(:disabled) {
            background: ${ThemeColors.gray[300]};
          }
        `;
    }
  }}
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Confirmation Modal Component
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <AlertTriangle 
            size={24} 
            color={variant === 'danger' ? ThemeColors.error.main : ThemeColors.primary.main} 
          />
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onCancel} disabled={isLoading}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <ModalMessage>{message}</ModalMessage>
        </ModalBody>
        
        <ModalActions>
          <ActionButton
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText}
          </ActionButton>
          <ActionButton
            variant={variant}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmText}
          </ActionButton>
        </ModalActions>
      </ModalContainer>
    </ModalOverlay>
  );
};
